<template>
  <div class="location-pay-settings__wrapper">
    <ui-loading v-if="loading.data" />
    <validation-observer
      v-else
      tag="div"
      v-slot="{ validate }"
      class="location-pay-settings"
    >
      <template v-if="showSelectPayTextSection">
        <div class="location-pay-settings__subtitle">
          Captions
        </div>

        <div class="location-pay-settings__inputs">
          <validation-provider
            tag="div"
            :name="$t('form.labels.caption.sublocations')"
            rules="required"
            mode="passive"
            v-slot="{ errors }"
            class="location-pay-settings__field"
          >
            <ui-input
              v-model="form.selectSubLocationText"
              :label="$t('form.labels.caption.sublocations')"
              class="location-pay-settings__input"
              required
              :error="errors[0]"
            />
            <img src="@/assets/images/pay/pay-sublocations.svg">
          </validation-provider>

          <validation-provider
            tag="div"
            :name="$t('form.labels.caption.groups')"
            rules="required"
            mode="passive"
            v-slot="{ errors }"
            class="location-pay-settings__field"
          >
            <ui-input
              v-model="form.selectGroupText"
              :label="$t('form.labels.caption.groups')"
              class="location-pay-settings__input"
              required
              :error="errors[0]"
            />
            <img src="@/assets/images/pay/pay-groups.svg">
          </validation-provider>

          <validation-provider
            tag="div"
            :name="$t('form.labels.caption.staffList')"
            rules="required"
            mode="passive"
            v-slot="{ errors }"
            class="location-pay-settings__field"
          >
            <ui-input
              v-model="form.selectStaffText"
              :label="$t('form.labels.caption.staffList')"
              class="location-pay-settings__input"
              required
              :error="errors[0]"
            />
            <img src="@/assets/images/pay/pay-staff-list.svg">
          </validation-provider>
        </div>
      </template>

      <div class="location-pay-settings__subtitle">
        Select layout
      </div>

      <div class="location-pay-settings__inputs">
        <validation-provider
          v-for="(item, index) in layoutList"
          :key="index"
          tag="div"
          mode="passive"
          class="location-pay-settings__field"
          rules="required"
          v-protected
        >
          <ui-radio
            :label="item.value"
            :value="form.paySelectLayout"
            name="pay-layout"
            class="location-pay-settings__input"
            required
            @input="handleLayoutSelect"
          >
            {{ item.label }}
          </ui-radio>

          <img
            :src="`${require(`@/assets/images/pay/${item.image}.svg`)}`"
            @click="handleLayoutSelect(item.value)"
          >
        </validation-provider>
      </div>

      <div class="location-pay-settings__subtitle">
        Banner on the payment success page
      </div>

      <div class="location-pay-settings__inputs">
        <validation-provider
          tag="div"
          :name="$t('form.labels.selectBanner')"
          mode="passive"
          v-slot="{ errors }"
          class="location-pay-settings__field"
        >
          <ui-select
            v-model="form.bannerVariant"
            :items="BANNER_VARIANTS"
            :label="$t('form.labels.selectBanner')"
            :placeholder="$t('form.placeholders.select')"
            filterable
            :error="errors[0]"
          />
        </validation-provider>
      </div>

      <div class="location-pay-settings__subtitle">
        Select layout
      </div>

      <img
        class="location-pay-settings__illustration"
        :src="`${require(`@/assets/images/pay/pay-room.svg`)}`"
      >

      <div class="location-pay-settings__inputs">
        <validation-provider
          tag="div"
          :name="$t('form.labels.enableLayout')"
          mode="passive"
          v-slot="{ errors }"
          class="location-pay-settings__field"
        >
          <ui-switcher
            v-model="form.enableServiceAreaLayout"
            :label="$t('form.labels.enableLayout')"
            :error="errors[0]"
          />
        </validation-provider>
      </div>

      <div
        v-if="form.enableServiceAreaLayout"
        class="location-pay-settings__inputs"
      >
        <validation-provider
          tag="div"
          :name="$t('form.labels.placeholderText')"
          mode="passive"
          rules="required|min:1|max:30"
          v-slot="{ errors }"
          class="location-pay-settings__field"
        >
          <ui-input
            v-model="form.serviceAreaPlaceholderText"
            maxlength="30"
            minlength="1"
            required
            :label="$t('form.labels.placeholderText')"
            :error="errors[0]"
            :counter="30"
          />
        </validation-provider>

        <validation-provider
          tag="div"
          :name="$t('form.labels.inputFieldText')"
          mode="passive"
          rules="required|min:1|max:5"
          v-slot="{ errors }"
          class="location-pay-settings__field"
        >
          <ui-input
            v-model="form.serviceAreaInputFieldText"
            maxlength="5"
            minlength="1"
            required
            :label="$t('form.labels.inputFieldText')"
            :error="errors[0]"
            :counter="5"
          />
        </validation-provider>

        <validation-provider
          tag="div"
          :name="$t('form.labels.requiredToFillIn')"
          mode="passive"
          rules="required"
          v-slot="{ errors }"
        >
          <ui-select
            v-model="form.isServiceAreaRequired"
            :items="REQUIRED_SELECTOR_ITEMS"
            :label="$t('form.labels.requiredToFillIn')"
            :label-info="$t('serviceAreaRequiredSelect.tooltip')"
            :placeholder="$t('form.placeholders.select')"
            required
            :error="errors[0]"
          />
        </validation-provider>
      </div>

      <ui-button
        action
        type="primary"
        :is-loading="loading.form"
        :disabled="loading.form"
        class="location-pay-settings__submit"
        @click.prevent="submit(validate)"
      >
        {{ $t('form.buttons.save') }}
      </ui-button>
    </validation-observer>
  </div>
</template>

<script>
import api from '@/api';
import { PAYMENT_PAGE_TYPE } from '@/config/paymentPageType';
import { REQUIRED_SELECTOR_ITEMS } from '@/config/requiredSelectorItems';

const BANNER_VARIANTS = [
  {
    value: null,
    name: 'Hidden',
  },
  {
    value: 'VARIANT_1',
    name: 'Variant 1',
  },
  {
    value: 'VARIANT_2',
    name: 'Variant 2',
  },
];

export default {
  name: 'LocationPaySettings',

  props: {
    location: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: {
        data: false,
        form: false,
      },
      form: {
        selectSubLocationText: '',
        paySelectLayout: '',
        selectGroupText: '',
        selectStaffText: '',
        bannerVariant: null,
        enableServiceAreaLayout: false,
        serviceAreaInputFieldText: '',
        isServiceAreaRequired: false,
        serviceAreaPlaceholderText: '',
      },
      BANNER_VARIANTS,
      REQUIRED_SELECTOR_ITEMS,
    };
  },

  computed: {
    showSelectPayTextSection() {
      return this.location.isMasterLocation || [PAYMENT_PAGE_TYPE.LIST, PAYMENT_PAGE_TYPE.GROUPS].includes(this.location.paymentPage);
    },
    layoutList() {
      return [
        {
          label: 'Layout 1',
          value: '1',
          image: 'pay-layout-1',
        },
        {
          label: 'Layout 2',
          value: '2',
          image: 'pay-layout-2',
        },
        {
          label: 'Layout 3',
          value: '3',
          image: 'pay-layout-3',
        },
      ];
    },
  },

  methods: {
    handleLayoutSelect(payload) {
      this.form.paySelectLayout = payload;
    },
    async loadPaySettings() {
      this.loading.data = true;

      try {
        const { data } = await api.locations.getPaySettingsConfig(this.location.id);

        this.form.selectSubLocationText = data.selectSubLocationText;
        this.form.paySelectLayout = data.paySelectLayout ? data.paySelectLayout.toString() : '0';
        this.form.selectGroupText = data.selectGroupText;
        this.form.selectStaffText = data.selectStaffText;
        this.form.bannerVariant = data.bannerVariant;
        this.form.enableServiceAreaLayout = data.enableServiceAreaLayout;
        this.form.serviceAreaPlaceholderText = data.serviceAreaPlaceholderText;
        this.form.serviceAreaInputFieldText = data.serviceAreaInputFieldText;
        this.form.isServiceAreaRequired = Boolean(data.isServiceAreaRequired);
      } catch (e) {
        this.$showServerError(e);
      } finally {
        this.loading.data = false;
      }
    },

    async submit(validate) {
      if (!await validate()) {
        return;
      }

      this.loading.form = true;

      try {
        await api.locations.setPaySettingsConfig(
          this.location.id,
          {
            selectSubLocationText: this.form.selectSubLocationText.trim(),
            paySelectLayout: Number(this.form.paySelectLayout),
            selectGroupText: this.form.selectGroupText.trim(),
            selectStaffText: this.form.selectStaffText.trim(),
            bannerVariant: this.form.bannerVariant,
            workplaceId: this.location.id,
            enableServiceAreaLayout: this.form.enableServiceAreaLayout,
            serviceAreaPlaceholderText: this.form.serviceAreaPlaceholderText,
            serviceAreaInputFieldText: this.form.serviceAreaInputFieldText,
            isServiceAreaRequired: this.form.isServiceAreaRequired,
          },
        );

        this.$emit('completed');

        this.$showSuccess();
      } catch (e) {
        this.$showServerError(e);
      } finally {
        this.loading.form = false;
      }
    },
  },

  mounted() {
    this.loadPaySettings();
  },
};
</script>

<style lang="scss">
.location-pay-settings {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__subtitle {
    color: #777;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
  }

  &__illustration {
    max-width: 295px;
  }

  &__inputs {
    display: flex;
    align-items: center;
    gap: 30px;
    width: 100%;

    @media screen and (max-width: 991px) {
      flex-direction: column;
    }
  }

  &__field {
    width: 295px;
    max-width: 100%;

    @media screen and (max-width: 991px) {
      width: 100%;
    }

    img {
      width: 100%;
    }
  }

  &__submit {
    width: 295px;
    max-width: 100%;
    margin-top: auto;
  }
}
</style>
